import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import qs from 'querystring';

import clsx from 'clsx';
import _get from 'lodash/get';
import _isEqual from 'lodash/isEqual';

import { useBrand } from '@atc/brand';
import { useFeatures } from '@atc/features';

import useUserData from '@kbbsdk/global-sdk/navigation/web/mykbb/useUserData';

import {
    Chip,
    IconButton,
} from 'reaxl';
import {
    sendClick,
} from 'reaxl-analytics';
import {
    emailSubscribeClick,
    emailSubscribeSignIn,
    facebookSubscribeSignIn,
    saveSearchSuccess,
} from 'reaxl-analytics-handlers';

import LocalStorage from '@/utilities/LocalStorage';

import remoteActions from '@/actions/srp/remoteActions';

import { sendSubscribeAlertClick } from '@/analytics/srp/srpEmailCaptureClickEvents';
import { saveSearchClick } from '@/analytics/srp/srpSearchEvents';
import useDataLayer from '@/analytics/useDataLayer';

import authDuck from '@/ducks/authDuck.js';
import recentSearchDuck from '@/ducks/recentSearchDuck.js';
import srpFiltersDuck from '@/ducks/srp/srpFiltersDuck.js';
import srpResultsDuck from '@/ducks/srp/srpResultsDuck.js';
import srpSortDuck from '@/ducks/srp/srpSortDuck.js';
import userDuck from '@/ducks/userDuck.js';

import useCustomContent from '@/hooks/useCustomContent.js';
import useSrpNavigation from '@/hooks/useSrpNavigation.js';

import emailRegistrationServiceTransformer from '@/transformers/emailRegistrationServiceTransformer.js';

import EmailSubscribeAlertContainer from './EmailSubscribeAlertContainer';

const renderSaveSearchLink = ({ handleSaveSearch, isIconCta, className, ...rest }) => {
    if (isIconCta) {
        return (
            <IconButton
                className={clsx('text-nowrap', className)}
                onClick={handleSaveSearch}
                label="Save Search"
                glyph="heart-o"
                size="small"
                {...rest}
            />
        );
    }
    return (
        <Chip
            uiContext="inquiry"
            label={rest.children}
            className={className}
            onClick={handleSaveSearch}
            {...rest}
        />
    );
};

const SaveSearchContainerComponent = ({
    className,
    isEmailCapture = false,
    isIconCta = true,
    ...rest
}) => {
    const dispatch = useDispatch();
    const dataLayer = useDataLayer();
    const navigateToSrp = useSrpNavigation();
    const { userInfo = {}, setUserInfo } = useUserData(); // used for KBB header dropdown only

    const { isBrand, brands } = useBrand();

    const search = useSelector(userDuck.selectors.getSavedSearch);
    const { saveSearchString } = search;
    const sort = useSelector(srpSortDuck.selectors.getDuckState, _isEqual);
    const requestValuesWithoutSort = useSelector(srpFiltersDuck.selectors.getRequestValues, _isEqual);
    const isLoggedIn = useSelector(authDuck.selectors.isLoggedIn);
    const hasResults = useSelector(srpResultsDuck.selectors.hasActiveResults);
    const showEmailSubscribeInline = useSelector(userDuck.selectors.getShowEmailSubscribeInline);

    const { isEnableCustomContent } = useCustomContent();

    const requestValues = useMemo(() => ({
        ...requestValuesWithoutSort,
        sortBy: sort,
    }), [requestValuesWithoutSort, sort]);

    const {
        personalization: [isPersonalizationEnabled] = [],
        SAVE_SEARCH_OFF: [isSaveSearchOff],
        SUBSCRIBE_SEARCH_ALERTS: [isEmailAlertsSubscribeActive],
    } = useFeatures([
        'personalization',
        'SAVE_SEARCH_OFF',
        'SUBSCRIBE_SEARCH_ALERTS',
    ]);

    // do not update selected filter values until the page is done loading
    const selectedFiltersValue = useSelector(srpFiltersDuck.selectors.getSelectedFilterChips, _isEqual);

    const hasSelectedFilterValues = !!selectedFiltersValue?.length;

    const [hasActiveSelectedFilterValues, setHasActiveSelectedFilterValues] = useState(hasSelectedFilterValues);

    useEffect(() => {
        if (hasResults) {
            setHasActiveSelectedFilterValues(hasSelectedFilterValues);
        }

    }, [hasResults, hasSelectedFilterValues]);

    const isSearchSavable = !isLoggedIn || !(search.title && search.title.length);
    const hasSearchParams = !!(requestValuesWithoutSort && Object.keys(requestValuesWithoutSort).length);
    const isSearchAlreadySubscribed = useSelector((state) => _get(recentSearchDuck.selectors.getRecentSearches(state), '[0].isSubscribed'));
    const isInlineSubscribeEnabled = !isLoggedIn && !isSearchAlreadySubscribed;
    const isInlineSubscribeCollapsed = isInlineSubscribeEnabled && !showEmailSubscribeInline;
    const showSaveSearchLink = (!isSaveSearchOff && hasSearchParams && isSearchSavable
        && isPersonalizationEnabled && hasActiveSelectedFilterValues);

    const setEmailAlerts = useCallback(() => {
        const parsedQuery = qs.parse(saveSearchString.slice(1));
        const transformedQuery = emailRegistrationServiceTransformer.transformSearch(parsedQuery);
        const searchData = isEmailCapture ? {
            ...transformedQuery,
            requestValues,
        } : transformedQuery;

        dispatch(remoteActions.setEmailAlertsSearch({
            search: searchData,
            onLogInSuccess: (response) => {
                const {
                    provider,
                } = response;

                if (provider === 'facebook') {
                    sendClick(facebookSubscribeSignIn);
                } else {
                    sendClick(emailSubscribeSignIn);
                }
            },

            // onSaveSuccess:.....
            onSubmit: (event, data) => {
                sendClick(emailSubscribeClick, event, data);
            },
        }));

        return searchData;
    }, [dispatch, isEmailCapture, requestValues, saveSearchString]);

    const handleSaveSearch = useCallback(async (event) => {

        const data = {
            searchString: saveSearchString,
            requestValues: JSON.stringify(requestValues),
        };

        // The event must be 'persisted' to be referenced in asynchronous functions like to one below; otherwise its
        // properties may be overwritten by React before the asynchronous function has a chance to read them.
        // See https://reactjs.org/docs/events.html
        if (event && event.persist) {
            event.persist();
        }
        if (isEmailCapture) {
            sendClick(sendSubscribeAlertClick, event, {});
        } else {
            sendClick(saveSearchClick, event, {
                isLoggedIn,
                search: requestValues,
                dataLayer,
            });
        }

        if (isEmailAlertsSubscribeActive && !isLoggedIn) {
            setEmailAlerts();
        } else if (isInlineSubscribeCollapsed) {
            const Storage = LocalStorage.getInstance();
            // eslint-disable-next-line react-hooks/rules-of-hooks
            Storage.useSession(true);
            Storage.clear('emailSubscribe');
            if (!isEmailCapture) {
                dispatch(userDuck.creators.showEmailSubscribeInline(true));
            } else {
                return setEmailAlerts();
            }
        } else {
            dispatch(userDuck.creators.saveSearchItem({
                data,
                onLogInSuccess: () => {
                    // fetcher call to get saved search data
                    // in case it's been already saved before

                    // push new instance of srp to history
                    navigateToSrp({ resetPagination: true });
                },
                onSaveSuccess: () => {
                    sendClick(saveSearchSuccess, event, { search: requestValues, dataLayer });
                    if (isBrand(brands.KBB_BRAND)) {
                        // increment count in header dropdown for KBB web component
                        setUserInfo({ ...userInfo, savedSearchesLength: (userInfo?.savedSearchesLength ? userInfo.savedSearchesLength : 0) + 1 });
                    }
                },
            }));
        }
        return true;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        dispatch,
        isEmailAlertsSubscribeActive,
        isInlineSubscribeCollapsed,
        isLoggedIn,
        navigateToSrp,
        requestValues,
        saveSearchString,
    ]);

    const saveCta = isEmailCapture ? (!isLoggedIn && (
        <EmailSubscribeAlertContainer
            withCustomContent={isEnableCustomContent}
            handleSaveSearch={handleSaveSearch}
        />
    )) : renderSaveSearchLink({ handleSaveSearch, isIconCta, className, ...rest }) || null;

    return showSaveSearchLink && saveCta;
};

export const SaveSearchContainer = memo(SaveSearchContainerComponent, _isEqual);

export default SaveSearchContainer;
